import { useState } from 'preact/hooks';
import { send, wallet, newAccount, bindCoin, fund, notesZat, planFund, FRESH_NOTE_MS, NotEnoughPrivate } from '../wallet.js';
import { useStore, refreshAccount } from '../store.js';
import { zec, parseZec, tokens } from '../format.js';
import { newPool, quoteBuy, maxBuyWithin } from '../../../src/core/curve.js';
import { baseFees, walletCap } from '../launch.js';
import { ApiError } from '../api.js';
import { Avatar, toast } from '../components/bits.js';

export function Create() {
  const s = useStore();
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [buy, setBuy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const g = s.info?.genesis;
  const createFee = g ? BigInt(g.createFeeZat) : 0n;
  const buyZat = buy.trim() === '' ? 0n : parseZec(buy);
  // A coin is launched from a fresh account paid with private notes, so the launch cannot be traced to your deposits.
  const balance = notesZat();
  const minTrade = g ? BigInt(g.minTradeZat) : 0n;

  let devTokens: bigint | null = null;
  // The creator's first buy is capped like everyone's (3% of the supply).
  let maxDevBuy: bigint | null = null;
  if (g) {
    const pool = newPool({
      totalSupply: BigInt(g.totalSupply),
      curveSupply: BigInt(g.curveSupply),
      virtualZat: BigInt(g.virtualZat),
      virtualTokens: BigInt(g.virtualTokens),
    });
    const cap = g.walletCapMs > 0 ? walletCap(g) : null;
    if (cap !== null) maxDevBuy = maxBuyWithin(pool, cap, baseFees(g));
    if (buyZat && buyZat > 0n) {
      try {
        devTokens = quoteBuy(pool, buyZat, baseFees(g)).tokensOut;
      } catch {
        devTokens = null;
      }
    }
  }
  const devTooBig = maxDevBuy !== null && buyZat !== null && buyZat > maxDevBuy;

  const symbolOk = /^[A-Z0-9]{1,10}$/.test(symbol);
  const imageOk = image === '' || /^https:\/\/[^\s"'<>]+$/.test(image);
  const total = createFee + (buyZat ?? 0n);
  const buyTooSmall = buyZat !== null && buyZat > 0n && buyZat < minTrade;
  const plan = wallet() && total <= balance ? planFund(total, 0n) : null;
  const freshMinutes = plan && plan.youngestMs < FRESH_NOTE_MS ? Math.max(1, Math.round(plan.youngestMs / 60_000)) : null;
  const canSubmit = !!wallet() && name.trim().length > 0 && symbolOk && imageOk && buyZat !== null && !buyTooSmall && !devTooBig && total <= balance && !busy;

  const submit = async (e: Event) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const minTokens = devTokens ? ((devTokens * 95n) / 100n).toString() : '0';
      const idx = newAccount();
      await fund(idx, total, 0n);
      const r = await send(
        { type: 'create', symbol, name: name.trim(), description: description.trim(), image: image.trim(), buyZat: (buyZat ?? 0n).toString(), minTokens },
        idx,
      );
      const token = (r.result as { token: string }).token;
      bindCoin(token, idx);
      toast(`$${symbol} is live`);
      refreshAccount();
      location.hash = `#/token/${token}`;
    } catch (x) {
      setErr(x instanceof NotEnoughPrivate ? 'Not enough private ZEC. Make ZEC private on the Wallet page first.' : x instanceof ApiError ? x.message : (x as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="page-narrow">
      <h1 class="page-title">Launch a coin</h1>
      <p class="lede">
        Your coin starts on a bonding curve priced in ZEC. There is no presale and no team allocation: everyone, including you, buys from the
        same curve. Launching costs {zec(createFee, 8)} ZEC.
      </p>
      {g && (
        <ul class="muted" style={{ marginTop: -8, marginBottom: 20, paddingLeft: 18 }}>
          <li>You earn {g.creatorBps / 100}% of every trade in your coin, for as long as it trades.</li>
          <li>The first minute is a fair launch: every buy fills at one price, so snipers and bundlers gain nothing.</li>
          <li>For the first hour no wallet, yours included, may hold more than {g.walletCapBps / 100}% of the coin.</li>
        </ul>
      )}
      {!wallet() && (
        <div class="notice warn" style={{ marginBottom: 20 }}>
          You need a wallet with some ZEC to launch. <a href="#/wallet">Set up a wallet</a>
        </div>
      )}
      <form onSubmit={submit}>
        <div class="grid2">
          <div class="field">
            <label for="name">Name</label>
            <div class="input">
              <input id="name" maxLength={32} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} placeholder="Shielded Cat" />
            </div>
          </div>
          <div class="field">
            <label for="sym">Ticker</label>
            <div class="input">
              <span class="unit" style={{ paddingRight: 0 }}>
                $
              </span>
              <input
                id="sym"
                maxLength={10}
                value={symbol}
                onInput={(e) => setSymbol((e.target as HTMLInputElement).value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="SCAT"
              />
            </div>
            <span class="hint">Up to 10 letters and digits.</span>
          </div>
        </div>
        <div class="field">
          <label for="desc">Description</label>
          <div class="input">
            <textarea id="desc" maxLength={280} value={description} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} placeholder="What is this coin about?" />
          </div>
          <span class="hint">{280 - description.length} characters left.</span>
        </div>
        <div class="field">
          <label for="img">Image link</label>
          <div class="input">
            <input id="img" value={image} onInput={(e) => setImage((e.target as HTMLInputElement).value.trim())} placeholder="https://…/picture.png" />
          </div>
          {imageOk ? <span class="hint">Optional. A direct https link to a square picture.</span> : <span class="err">The link must start with https://</span>}
        </div>
        <div class="field">
          <label for="buy">Buy at launch</label>
          <div class="input">
            <input id="buy" inputMode="decimal" value={buy} onInput={(e) => setBuy((e.target as HTMLInputElement).value)} placeholder="0" />
            <span class="unit">ZEC</span>
          </div>
          {buyTooSmall ? (
            <span class="err">The smallest buy is {zec(minTrade, 8)} ZEC.</span>
          ) : devTooBig ? (
            <span class="err">
              At launch you may buy at most {zec(maxDevBuy!, 6)} ZEC ({(g?.walletCapBps ?? 0) / 100}% of the coin).
            </span>
          ) : (
            <span class="hint">
              {devTokens ? `You get about ${tokens(devTokens)} ${symbol || 'tokens'} at the opening price.` : 'Optional. Be the first buyer of your own coin.'}
            </span>
          )}
        </div>

        {(name || symbol) && (
          <div class="panel" style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
            <Avatar token={{ id: symbol || 'x', symbol: symbol || '?', image: imageOk ? image : '' }} size={56} />
            <div>
              <div class="display" style={{ fontSize: 34 }}>
                ${symbol || 'TICKER'}
              </div>
              <div class="muted">{name || 'Name'}</div>
            </div>
          </div>
        )}

        <div class="quote">
          <div>
            <span class="muted">Total</span>
            <strong>{zec(total, 8)} ZEC</strong>
          </div>
          <div>
            <span class="muted">Your private balance</span>
            <span>{zec(balance, 8)} ZEC</span>
          </div>
        </div>
        {freshMinutes !== null && (
          <div class="notice warn" style={{ marginBottom: 12, fontSize: 13 }}>
            This launch uses private ZEC made {freshMinutes} min ago. Anyone reading the log could link the coin to your deposit by timing.
            Waiting longer is safer.
          </div>
        )}
        {total > balance && wallet() && (
          <div class="notice warn" style={{ marginBottom: 12 }}>
            Launches are paid from private ZEC, so nobody can tie the coin to your deposits. <a href="#/wallet">Make ZEC private</a>
          </div>
        )}
        {err && (
          <div class="notice bad" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}
        <button class="btn brass block" disabled={!canSubmit}>
          {busy ? 'Launching…' : symbol ? `Launch $${symbol}` : 'Launch coin'}
        </button>
      </form>
    </div>
  );
}
